import { useEffect, useRef, useState } from "react";

/**
 * Initialises DuckDB-WASM once per component tree.
 * Uses the jsDelivr CDN bundles so no webpack/CRA config is needed.
 *
 * Returns { ready, error, dbRef }
 *   ready  — true once the engine is loaded
 *   error  — string if init failed
 *   dbRef  — React ref whose .current is the AsyncDuckDB instance
 */
export function useDuckDB() {
  const dbRef  = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const duckdb = await import("@duckdb/duckdb-wasm");

        // jsDelivr CDN bundles — WASM is fetched at runtime, no webpack config needed
        const bundle = await duckdb.selectBundle(duckdb.getJsDelivrBundles());

        // Create a blob worker so CRA's CSP doesn't block the inline script
        const workerUrl = URL.createObjectURL(
          new Blob([`importScripts("${bundle.mainWorker}");`], { type: "text/javascript" })
        );

        const worker = new Worker(workerUrl);
        const db     = new duckdb.AsyncDuckDB(new duckdb.VoidLogger(), worker);
        await db.instantiate(bundle.mainModule, bundle.pthreadWorker);

        if (!cancelled) {
          dbRef.current = db;
          setReady(true);
        }
      } catch (e) {
        console.error("DuckDB-WASM init failed:", e);
        if (!cancelled) setError(e.message);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return { ready, error, dbRef };
}
